import { memo, ReactElement, useCallback, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { IClientOption } from '~/store/client/types';
import MenuList from '~/ui/components/inputs/SelectWithoutAnimation/components/MenuList';
import ClientMultiSelect from '~/ui/pages/Clients/reusable/ClientMultiSelect';
import useAllocationChangeTracking from '../../../shared/hooks/useAllocationChangeTracking';
import { ClientWithAllocation, IEditAllocationFormValues } from '../../../shared/model/types';
import useSelectAdditionalStyles from '../../hooks/useSelectAdditionalStyles';
import { normalizeClientOptionsByTeamToAllocations } from '../../utils';
import {
  buildOriginalAllocationIdsByClientId,
  buildSelectedOptions,
  getSelectionDiff,
} from './AllocationClientsSelectGU.utils';

interface IProps {
  loading: boolean;
  loadMore: VoidFunction;
  teamMemberIdx: number;
  updateAllocations: (selectedClients: ClientWithAllocation[]) => void;
}

const AllocationClientsSelectGU = ({
  loading,
  loadMore,
  teamMemberIdx,
  updateAllocations,
}: IProps): ReactElement => {
  const { watch } = useFormContext<IEditAllocationFormValues>();

  const { additionalStyleHandler } = useSelectAdditionalStyles(teamMemberIdx);

  const teamMembersAllocations = watch('teamMembersAllocations');

  const teamMemberAllocations = useMemo(
    () => teamMembersAllocations[teamMemberIdx],
    [teamMembersAllocations, teamMemberIdx],
  );

  const clientsAllocationsFields = useMemo(
    () => teamMemberAllocations.clientsAllocations,
    [teamMemberAllocations.clientsAllocations],
  );

  const { markDeleted, unmarkDeleted } = useAllocationChangeTracking<IEditAllocationFormValues>({
    changedField: 'changedAllocationsIds',
    deletedField: 'deletedAllocationsIds',
  });

  const previousSelectedIdsRef = useRef<number[]>(
    clientsAllocationsFields.map(field => field.clientId),
  );
  const originalAllocationIdsByClientIdRef = useRef(
    buildOriginalAllocationIdsByClientId(clientsAllocationsFields),
  );

  const groupedExistingClientsByClientId = useMemo(
    () => new Map(clientsAllocationsFields.map(field => [field.clientId, field])),
    [clientsAllocationsFields],
  );

  const handleSelectClientOptionsByTeam = useCallback(
    (options: IClientOption[]) => {
      const selectedIds = options.map(option => option.id);
      const { removedIds, retainedIds } = getSelectionDiff(
        previousSelectedIdsRef.current,
        selectedIds,
      );

      removedIds.forEach(clientId => {
        const allocationId = originalAllocationIdsByClientIdRef.current.get(clientId);
        if (allocationId) {
          markDeleted(allocationId);
        }
      });

      retainedIds.forEach(clientId => {
        const allocationId = originalAllocationIdsByClientIdRef.current.get(clientId);
        if (allocationId) {
          unmarkDeleted(allocationId);
        }
      });

      previousSelectedIdsRef.current = selectedIds;
      const selectedOptions = buildSelectedOptions(options, clientsAllocationsFields);

      updateAllocations(
        normalizeClientOptionsByTeamToAllocations(selectedOptions, null).map(
          client => groupedExistingClientsByClientId.get(client.clientId) ?? client,
        ),
      );
    },
    [
      clientsAllocationsFields,
      groupedExistingClientsByClientId,
      markDeleted,
      unmarkDeleted,
      updateAllocations,
    ],
  );

  return (
    <ClientMultiSelect
      loading={loading}
      loadMore={loadMore}
      label="Select Clients to Assign"
      customComponents={{ MenuList }}
      additionalStyleHandler={additionalStyleHandler}
      onSelect={handleSelectClientOptionsByTeam}
    />
  );
};

export default memo(AllocationClientsSelectGU);
